import type { TenantProductScope } from "@/core/auth/current-tenant";

export type LogisticsRoleFocus =
  | "dispatcher"
  | "broker"
  | "ops_manager"
  | "finance"
  | "admin_owner";

export type OnboardingScopeCard = {
  product: TenantProductScope;
  label: string;
  summary: string;
  mode: string;
};

export type OnboardingFirstAction = {
  title: string;
  description: string;
  href: string;
};

export type OnboardingPlan = {
  tenantName: string;
  welcomeTitle: string;
  primaryProduct: TenantProductScope;
  productLabel: string;
  scopeCards: OnboardingScopeCard[];
  agentIntro: {
    title: string;
    body: string;
    capabilities: string[];
  };
  abilityMatrix: {
    canDo: string[];
    needsApproval: string[];
    cannotDo: string[];
  };
  firstActions: OnboardingFirstAction[];
  setupChecklist: string[];
};

const PRODUCT_PRIORITY: TenantProductScope[] = ["full_agentic_tms", "haulbase_agent", "headless_tms"];

const PRODUCT_LABELS: Record<TenantProductScope, string> = {
  full_agentic_tms: "Haulbase Agentic TMS",
  haulbase_agent: "Haulbase Agent",
  headless_tms: "Headless Haulbase",
};

const SCOPE_CARDS: Record<TenantProductScope, Omit<OnboardingScopeCard, "product">> = {
  full_agentic_tms: {
    label: PRODUCT_LABELS.full_agentic_tms,
    summary: "The full operating workspace for loads, exceptions, approvals, documents, financials, and the embedded agent.",
    mode: "System of record",
  },
  haulbase_agent: {
    label: PRODUCT_LABELS.haulbase_agent,
    summary: "An external-TMS overlay that reads operational signals and drafts freight work for review.",
    mode: "Read and draft",
  },
  headless_tms: {
    label: PRODUCT_LABELS.headless_tms,
    summary: "API-first command gateway, route catalog, idempotent traces, approvals, and audit controls.",
    mode: "Headless control plane",
  },
};

const ROLE_FIRST_ACTIONS: Record<LogisticsRoleFocus, OnboardingFirstAction[]> = {
  dispatcher: [
    {
      title: "Review today's exception queue",
      description: "Start with stale tracking, appointment risk, POD gaps, and loads that need human attention.",
      href: "/inbox",
    },
  ],
  broker: [
    {
      title: "Draft carrier outreach for uncovered loads",
      description: "Use the agent to prepare outreach and recommended next actions without committing externally.",
      href: "/agent",
    },
  ],
  ops_manager: [
    {
      title: "Inspect approval backlog and operational risk",
      description: "See which decisions are waiting, why they need review, and what evidence supports them.",
      href: "/inbox",
    },
  ],
  finance: [
    {
      title: "Review billing, settlement, and payment holds",
      description: "Focus on money-impacting work that stays approval-gated in the current product scope.",
      href: "/agentic-tms",
    },
  ],
  admin_owner: [
    {
      title: "Configure approvals, users, and integration gates",
      description: "Confirm who can approve work, which products are enabled, and where production actions stop.",
      href: "/settings/autonomy",
    },
  ],
};

export function resolvePrimaryOnboardingProduct(products: readonly TenantProductScope[]): TenantProductScope {
  return PRODUCT_PRIORITY.find((product) => products.includes(product)) ?? "headless_tms";
}

export function buildOnboardingPlan(args: {
  tenantName: string;
  products: readonly TenantProductScope[];
  roleFocus: LogisticsRoleFocus;
}): OnboardingPlan {
  const primaryProduct = resolvePrimaryOnboardingProduct(args.products);
  const orderedProducts = PRODUCT_PRIORITY.filter((product) => args.products.includes(product));

  return {
    tenantName: args.tenantName,
    welcomeTitle: `Welcome to Haulbase, ${args.tenantName}`,
    primaryProduct,
    productLabel: PRODUCT_LABELS[primaryProduct],
    scopeCards: orderedProducts.map((product) => ({ product, ...SCOPE_CARDS[product] })),
    agentIntro: {
      title: "Meet the Haulbase Agent",
      body: "The Haulbase Agent is an operations teammate for logistics teams. It watches freight context, drafts work, and keeps risky actions behind approval gates.",
      capabilities: [
        "Watches active freight and highlights exceptions before they spread.",
        "Drafts customer, carrier, document, billing, and operations work from tenant-scoped context.",
        "Builds approval packets with evidence instead of silently committing freight changes.",
      ],
    },
    abilityMatrix: {
      canDo: [
        "Read active loads, stops, customer references, carrier context, and audit history.",
        "Summarize risk, stale tracking, missing documents, customer SOP gaps, and next recommended action.",
        "Draft internal tasks, annotations, approval packets, and operator-ready messages.",
      ],
      needsApproval: [
        "Customer-facing messages, carrier tenders, rate-impacting edits, and settlement or payment actions.",
        "External system writes, load-board commitments, accounting pushes, and production workflow promotion.",
      ],
      cannotDo: [
        "Silently commit risky freight changes outside the tenant's approved product scope.",
        "Bypass tenant isolation, approval routing, API signatures, idempotency, or audit logging.",
      ],
    },
    firstActions: ROLE_FIRST_ACTIONS[args.roleFocus],
    setupChecklist: buildSetupChecklist(primaryProduct),
  };
}

function buildSetupChecklist(product: TenantProductScope): string[] {
  if (product === "full_agentic_tms") {
    return [
      "Invite dispatchers, managers, finance users, and admins.",
      "Import or create loads, customers, carriers, documents, and SOPs.",
      "Confirm approval policies for customer, carrier, money, and rate-impacting work.",
      "Run the first exception review from the Agentic TMS workspace.",
    ];
  }

  if (product === "haulbase_agent") {
    return [
      "Connect the external TMS source in sandbox or read-only mode.",
      "Map escalation owners, approval routing, stale tracking thresholds, and verification rules.",
      "Replay sample events before shadow-mode review.",
      "Promote to production read-and-draft only after operator and broker signoff.",
    ];
  }

  return [
    "Create agent credentials and verify allowed scopes.",
    "Validate route catalog, signed commands, webhook signatures, and idempotency behavior.",
    "Review command traces and approval outcomes before live usage.",
    "Keep production actions behind approval gates until the tenant passes readiness checks.",
  ];
}
